import { ThemeProvider } from '@mui/material/styles';
import {
  cleanup,
  fireEvent,
  render,
  screen,
  waitFor,
} from '@testing-library/react';
import React from 'react';
import { Provider } from 'react-redux';
import { MockedFunction, vi } from 'vitest';

import {
  useGetMachinesQuery,
  useProvisionLinkMutation,
  useSetWifiMutation,
} from '../../features/thirdPartyIntegrations';
import emLegacyTheme from '../../theme/emLegacyTheme';
import ThirdPartyIntegrations, {
  ThirdPartyIntegrationsProps,
} from './ThirdPartyIntegrations';
import store from './store';

vi.mock('../../features/thirdPartyIntegrations', () => ({
  useGetMachinesQuery: vi.fn(),
  useProvisionLinkMutation: vi.fn(),
  useSetWifiMutation: vi.fn(),
}));

const mockedUseGetMachinesQuery = useGetMachinesQuery as MockedFunction<
  typeof useGetMachinesQuery
>;
const mockedUseProvisionLinkMutation =
  useProvisionLinkMutation as MockedFunction<typeof useProvisionLinkMutation>;
const mockedUseSetWifiMutation = useSetWifiMutation as MockedFunction<
  typeof useSetWifiMutation
>;

const mockMachineData = [
  {
    customer: 'Customer 1',
    id: 1,
    mac_address: '00:11:22:33:44:55',
    machine_model: 'Model A',
    name: 'Test Machine 1',
    partner: 'Partner 1',
    serial_number: 'SN001',
    type: 'Element-T',
    uuid: 'uuid-1',
  },
  {
    customer: 'Customer 2',
    id: 2,
    mac_address: '00:11:22:33:44:66',
    machine_model: 'Model B',
    name: 'Test Machine 2',
    partner: 'Partner 2',
    serial_number: 'SN002',
    type: 'Element-D',
    uuid: 'uuid-2',
  },
];

const defaultProps = {
  apiHost: 'https://test-api.example.com',
  currentCustomerGroupUuid: 'test-customer-group-uuid',
};

const TestWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Provider store={store}>
    <ThemeProvider theme={emLegacyTheme}>{children}</ThemeProvider>
  </Provider>
);

const renderWithProviders = (props: ThirdPartyIntegrationsProps) => {
  return render(
    <TestWrapper>
      <ThirdPartyIntegrations {...props} />
    </TestWrapper>,
  );
};

describe('ThirdPartyIntegrations', () => {
  const mockProvisionLinkMutation = vi.fn();
  const mockSetWifiMutation = vi.fn();

  beforeEach(() => {
    vi.clearAllMocks();

    mockedUseGetMachinesQuery.mockReturnValue({
      data: mockMachineData,
      error: undefined,
      isLoading: false,
      refetch: vi.fn(),
    });

    mockedUseProvisionLinkMutation.mockReturnValue([
      mockProvisionLinkMutation.mockImplementation(() => ({
        unwrap: () => Promise.resolve(),
      })),
      {
        error: undefined,
        isLoading: false,
        reset: vi.fn(),
      },
    ]);

    mockedUseSetWifiMutation.mockReturnValue([
      mockSetWifiMutation.mockImplementation(() => ({
        unwrap: () => Promise.resolve(),
      })),
      {
        error: undefined,
        isLoading: false,
        reset: vi.fn(),
      },
    ]);
  });

  afterEach(() => {
    cleanup();
  });

  test('renders component with title', async () => {
    renderWithProviders(defaultProps);

    expect(screen.getByText('Third-Party Integrations')).toBeInTheDocument();
  });

  test('renders filter input', async () => {
    renderWithProviders(defaultProps);

    expect(
      screen.getByPlaceholderText(
        'Filter by serial number, name, or MAC address...',
      ),
    ).toBeInTheDocument();
  });

  test('fetches and displays machine data', async () => {
    renderWithProviders(defaultProps);

    await waitFor(() => {
      expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      expect(screen.getByText('SN001')).toBeInTheDocument();
    });
  });

  test('displays empty state when no machines', async () => {
    mockedUseGetMachinesQuery.mockReturnValue({
      data: [],
      error: undefined,
      isLoading: false,
      refetch: vi.fn(),
    });

    renderWithProviders(defaultProps);

    await waitFor(() => {
      expect(screen.getByText('No machines available.')).toBeInTheDocument();
    });
  });

  test('renders checkboxes for selection', async () => {
    renderWithProviders(defaultProps);

    await waitFor(() => {
      expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
    });

    const checkboxes = screen.getAllByRole('checkbox');
    expect(checkboxes.length).toBeGreaterThan(0);
  });

  test('allows individual machine selection', async () => {
    renderWithProviders(defaultProps);

    await waitFor(() => {
      expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
    });

    const checkboxes = screen.getAllByRole('checkbox');
    const firstMachineCheckbox = checkboxes[1]; // Skip header checkbox

    fireEvent.click(firstMachineCheckbox);

    expect(firstMachineCheckbox).toBeChecked();
  });

  test('calls API with filter when typing in search', async () => {
    renderWithProviders(defaultProps);

    const filterInput = screen.getByPlaceholderText(
      'Filter by serial number, name, or MAC address...',
    );

    fireEvent.change(filterInput, { target: { value: 'SN001' } });

    // Wait for debounce and verify the hook was called with filter
    await waitFor(
      () => {
        expect(mockedUseGetMachinesQuery).toHaveBeenCalledWith(
          { serial_filter: 'SN001' },
          expect.objectContaining({
            pollingInterval: 0,
            refetchOnMountOrArgChange: true,
          }),
        );
      },
      { timeout: 2000 },
    );
  });

  describe('Anylink Integration - Provision Functionality', () => {
    test('provision button is disabled when no machines selected', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const provisionButton = screen.getByRole('button', {
        name: /provision/i,
      });
      expect(provisionButton).toBeDisabled();
    });

    test('provision button is disabled when required fields are empty', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      const provisionButton = screen.getByRole('button', {
        name: /provision/i,
      });
      expect(provisionButton).toBeDisabled();
    });

    test('provision button is enabled when machines selected and fields filled', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      const linkNameInput = screen.getByTestId('anylink-link-name-input');
      const serialNumberInput = screen.getByTestId(
        'anylink-serial-number-input',
      );

      fireEvent.change(linkNameInput, { target: { value: 'Test Link' } });
      fireEvent.change(serialNumberInput, { target: { value: 'TEST123' } });

      const provisionButton = screen.getByRole('button', {
        name: /provision/i,
      });
      expect(provisionButton).toBeEnabled();
    });

    test('handles successful provision request', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      const linkNameInput = screen.getByTestId('anylink-link-name-input');
      const serialNumberInput = screen.getByTestId(
        'anylink-serial-number-input',
      );

      fireEvent.change(linkNameInput, { target: { value: 'Test Link' } });
      fireEvent.change(serialNumberInput, { target: { value: 'TEST123' } });

      const provisionButton = screen.getByRole('button', {
        name: /provision/i,
      });
      fireEvent.click(provisionButton);

      await waitFor(() => {
        expect(mockProvisionLinkMutation).toHaveBeenCalledWith({
          apiHost: 'https://test-api.example.com',
          data: {
            customer_group_uuid: 'test-customer-group-uuid',
            link_name: 'Test Link',
            managed_machines: ['uuid-1'],
            serial_number: 'TEST123',
          },
        });
      });
    });

    test('handles provision request error', async () => {
      const mockError = {
        data: { message: 'Provision failed' },
        status: 400,
      };

      mockedUseProvisionLinkMutation.mockReturnValue([
        mockProvisionLinkMutation,
        {
          error: mockError,
          isLoading: false,
          reset: vi.fn(),
        },
      ]);

      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      await waitFor(() => {
        expect(screen.getByText('Provision failed')).toBeInTheDocument();
      });
    });

    test('shows loading state during provision', async () => {
      mockedUseProvisionLinkMutation.mockReturnValue([
        mockProvisionLinkMutation,
        {
          error: undefined,
          isLoading: true,
          reset: vi.fn(),
        },
      ]);

      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      expect(screen.getByText('Provisioning...')).toBeInTheDocument();
    });
  });

  describe('WiFi Configuration Functionality', () => {
    test('wifi button is disabled when required fields are empty', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const wifiButton = screen.getByRole('button', { name: /set wifi/i });
      expect(wifiButton).toBeDisabled();
    });

    test('wifi button is enabled when all fields filled', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      const linkNameInput = screen.getByTestId('anylink-link-name-input');
      const serialNumberInput = screen.getByTestId(
        'anylink-serial-number-input',
      );
      const wifiSSIDInput = screen.getByTestId('anylink-wifi-ssid-input');
      const wifiPasswordInput = screen.getByTestId(
        'anylink-wifi-password-input',
      );

      fireEvent.change(linkNameInput, { target: { value: 'Test Link' } });
      fireEvent.change(serialNumberInput, { target: { value: 'TEST123' } });
      fireEvent.change(wifiSSIDInput, { target: { value: 'TestNetwork' } });
      fireEvent.change(wifiPasswordInput, { target: { value: 'password123' } });

      const wifiButton = screen.getByRole('button', { name: /set wifi/i });
      expect(wifiButton).toBeEnabled();
    });

    test('handles successful wifi configuration', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      const linkNameInput = screen.getByTestId('anylink-link-name-input');
      const serialNumberInput = screen.getByTestId(
        'anylink-serial-number-input',
      );
      const wifiSSIDInput = screen.getByTestId('anylink-wifi-ssid-input');
      const wifiPasswordInput = screen.getByTestId(
        'anylink-wifi-password-input',
      );

      fireEvent.change(linkNameInput, { target: { value: 'Test Link' } });
      fireEvent.change(serialNumberInput, { target: { value: 'TEST123' } });
      fireEvent.change(wifiSSIDInput, { target: { value: 'TestNetwork' } });
      fireEvent.change(wifiPasswordInput, { target: { value: 'password123' } });

      const wifiButton = screen.getByRole('button', { name: /set wifi/i });
      fireEvent.click(wifiButton);

      await waitFor(() => {
        expect(mockSetWifiMutation).toHaveBeenCalledWith({
          apiHost: 'https://test-api.example.com',
          data: {
            customer_group_uuid: 'test-customer-group-uuid',
            link_name: 'Test Link',
            password: 'password123',
            serial_number: 'TEST123',
            ssid: 'TestNetwork',
          },
        });
      });
    });
  });

  describe('Error Handling', () => {
    test('displays error when machine fetch fails', async () => {
      const mockError = {
        data: { message: 'Network error' },
        status: 500,
      };

      mockedUseGetMachinesQuery.mockReturnValue({
        data: undefined,
        error: mockError,
        isLoading: false,
        refetch: vi.fn(),
      });

      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Network error')).toBeInTheDocument();
      });
    });

    test('handles missing API host for provision', async () => {
      renderWithProviders({ currentCustomerGroupUuid: 'test-uuid' });

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      const linkNameInput = screen.getByTestId('anylink-link-name-input');
      const serialNumberInput = screen.getByTestId(
        'anylink-serial-number-input',
      );

      fireEvent.change(linkNameInput, { target: { value: 'Test Link' } });
      fireEvent.change(serialNumberInput, { target: { value: 'TEST123' } });

      const provisionButton = screen.getByRole('button', {
        name: /provision/i,
      });
      fireEvent.click(provisionButton);

      expect(mockProvisionLinkMutation).not.toHaveBeenCalled();
    });
  });

  describe('Batch Operations', () => {
    test('displays selected machine count', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);

      expect(checkboxes[1]).toBeChecked();
    });

    test('provisions multiple selected machines', async () => {
      renderWithProviders(defaultProps);

      await waitFor(() => {
        expect(screen.getByText('Test Machine 1')).toBeInTheDocument();
      });

      const linkNameInput = screen.getByTestId('anylink-link-name-input');
      const serialNumberInput = screen.getByTestId(
        'anylink-serial-number-input',
      );

      fireEvent.change(linkNameInput, { target: { value: 'Test Link' } });
      fireEvent.change(serialNumberInput, { target: { value: 'TEST123' } });

      const checkboxes = screen.getAllByRole('checkbox');
      fireEvent.click(checkboxes[1]);
      fireEvent.click(checkboxes[2]);

      const provisionButton = screen.getByTestId('anylink-provision-button');
      fireEvent.click(provisionButton);

      await waitFor(() => {
        expect(mockProvisionLinkMutation).toHaveBeenCalledWith({
          apiHost: 'https://test-api.example.com',
          data: expect.objectContaining({
            managed_machines: ['uuid-1', 'uuid-2'],
          }),
        });
      });
    }, 15000);
  });
});
